import { ErrorMessage, Field, Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { bindActionCreators } from 'redux';
import _actions from '../actions';
import DateTimeInfo from '../components/common/DateTimeInfo';
import DismissableAlert from '../components/common/DismissableAlert';
import ExpiredPackages from '../components/common/ExpiredPackages';
import { FormBackButton } from '../components/common/FormBackButton';
import { FormHeader } from '../components/common/FormHeader';
import { FormReset } from '../components/common/FormReset';
import { FormResetButton } from '../components/common/FormResetButton';
import { FormSubheader } from '../components/common/FormSubheader';
import { FormSubmit } from '../components/common/FormSubmit';
import { FormSubmitButton } from '../components/common/FormSubmitButton';
import { FormWrapper } from '../components/common/FormWrapper';
import { AddOnTotals } from '../components/package/AddOnTotal';
import { PackageAddOns } from '../components/package/PackageAddOns';
import emptySale from '../models/sale';
import { first } from '../utils';
import addOnKey from '../utils/addOnKey';
import getItemModsForItemExpanded from '../utils/getModsForGetItemExpanded';
import hasAddOns from '../utils/hasAddOns';
import modMax from '../utils/modMax';
import modMin from '../utils/modMin';
import modReqd from '../utils/modReqd';
import packageKey from '../utils/packageKey';
import availTimes from '../utils/availTimes';
import './AddOns.css';
import ReservationTimer from './ReservationTimer';
import { isAfter } from 'date-fns';
import { parseISOLocal } from '../utils/date';

function autoAddMinMods(sale, inventory, actions) {
  let nextSale = { ...sale };
  const pkgsThatNeedUpdate = [];
  const inventoryPkgs = inventory.packages[packageKey(sale.day)] || [];
  for (var pkgId in sale.packages) {
    const pkg = first(inventoryPkgs, p => p.item_id == pkgId);
    const modMinNum = modMin(pkg);
    const isModReqd = modReqd(pkg);
    var pkgAddOns = inventory.addOns[addOnKey(sale.day, pkg)] || [];
    if (isModReqd && modMinNum > 0 && pkgAddOns && pkgAddOns.length === 1) {
      pkgsThatNeedUpdate.push({ pkg, pkgAddOns });
      for (var addOn of pkgAddOns) {
        nextSale = {
          ...nextSale,
          packageAddOns: {
            ...nextSale.packageAddOns,
            [pkgId]: {
              ...(nextSale.packageAddOns[pkgId] || {}),
              [addOn.item_id]: { qty: modMinNum }
            }
          }
        };
      }
    }
  }
  if (pkgsThatNeedUpdate.length > 0) {
    actions.updateSale(nextSale);
    pkgsThatNeedUpdate.forEach(pkgObj => {
      actions.getItemExpanded(pkgObj.pkg);
    });
    return true;
  }
  return false;
}

function getAddOnIdsForAddOnsWithTimes(sale, inventory) {
  const inventoryPkgs = inventory.packages[packageKey(sale.day)] || [];

  const addOnItemsWithTimes = [];
  Object.keys(sale.packages).map(pkgId => {
    const pkg = first(inventoryPkgs, p => p.item_id == pkgId);
    if (pkg == null) return null;

    const addOns = inventory.addOns[addOnKey(sale.day, pkg)] || [];
    addOns.forEach(addOn => {
      const _availTimes = availTimes(addOn, sale);
      const now = new Date();
      const todaysTimes = _availTimes
        ? Object.keys(_availTimes)
            // make sure it's available
            .filter(timeProp => {
              var timeObj = _availTimes[timeProp];
              var date = parseISOLocal(
                timeObj['prop-start_time'].replace(
                  /(\d{4})(\d{2})(\d{2}) (.+)/,
                  '$1-$2-$3T$4'
                )
              );
              var timeIsFuture = isAfter(date, now);
              return timeObj['prop-m4s'] > 0 && timeIsFuture;
            })
        : [];
      const hasTimes = todaysTimes !== null && todaysTimes.length > 0;
      if (hasTimes) {
        addOnItemsWithTimes.push(addOn.item_id);
      }
    });
  });

  return addOnItemsWithTimes;
}

class AddOnsContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loadingAddons: false,
      addOnItemsWithTimes: []
    };
  }

  checkAddOnsWithTimesHaveSelectedTime(sale, inventory, packageAddOns) {
    const { addOnItemsWithTimes } = this.state;
    if (addOnItemsWithTimes.length === 0) return;

    const inventoryPkgs = inventory.packages[packageKey(sale.day)] || [];
    let hasAddOnWithMissingTime = false;

    Object.keys(sale.packages).map(pkgId => {
      const pkg = first(inventoryPkgs, p => p.item_id == pkgId);
      if (pkg == null) return;

      const pkgAddOn = packageAddOns[pkgId] || {};
      Object.keys(pkgAddOn).forEach(addOnId => {
        const id = parseInt(addOnId, 10);
        // Only check addOnIds that are present in the addOnItemsWithTimes array
        if (addOnItemsWithTimes.indexOf(id) === -1) return;

        const addOn = packageAddOns[pkgId][addOnId];
        if (addOn == null) {
          return;
        }

        if (!addOn.time || addOn.time.length === 0) {
          hasAddOnWithMissingTime = true;
        }
      });
    });

    const newState = { ...this.state, hasAddOnWithMissingTime };
    this.setState(newState);
  }

  componentDidMount() {
    const { sale, actions, done } = this.props;
    if (sale.id == null || sale.day == null || Object.keys(sale.packages) < 1) {
      return actions.transitionTo('/');
    }

    if (done) {
      return actions.transitionTo('/thanks');
    }

    this.setState({ loadingAddons: true }, () => {
      actions.getAddOns().then(() => {
        const { inventory, sale } = this.props;
        if (!hasAddOns(inventory, sale)) {
          return actions.transitionTo('/review');
        }
        this.setState({ loadingAddons: false }, () => {
          this.setState({
            modsAutoAdded: autoAddMinMods(sale, inventory, actions),
            addOnItemsWithTimes: getAddOnIdsForAddOnsWithTimes(sale, inventory),
            addOnTimesSelected: false
          });
        });
      });
    });
  }

  render() {
    const { error, sale, inventory, actions, loading, config } = this.props;
    const {
      loadingAddons,
      modsAutoAdded,
      hasAddOnWithMissingTime
    } = this.state;
    const inventoryPkgs = inventory.packages[packageKey(sale.day)] || [];

    const showLoading =
      inventory.loading ||
      loadingAddons ||
      (inventory.loadingItemExpanded > 0 && modsAutoAdded);

    return (
      <FormWrapper>
        <FormHeader>
          <ReservationTimer sale={sale}>
            Buy Tickets: Select Ticket Add-Ons
          </ReservationTimer>
        </FormHeader>
        <FormSubheader>
          <DateTimeInfo date={sale.day} config={config} />
          <ExpiredPackages sale={sale} actions={actions} />
        </FormSubheader>
        {showLoading ? (
          'Loading ...'
        ) : (
          <Formik
            initialValues={{
              packageAddOns: sale.packageAddOns
            }}
            enableReinitialize
            validate={({ packageAddOns }) => {
              let errors = {};

              const packageAddOnsErrors = [];
              Object.keys(sale.packages).map(pkgId => {
                const numPkgs = sale.packages[pkgId].qty;
                if (numPkgs < 1) return;

                const pkg = first(inventoryPkgs, p => p.item_id == pkgId);
                if (pkg == null) return;

                const addOns = inventory.addOns[addOnKey(sale.day, pkg)] || [];
                if (addOns == null || addOns.length < 1) {
                  return;
                }

                const currentPkgAddOns = packageAddOns[pkgId] || {};

                let hasQty = false;
                let totalQty = 0;

                addOns.forEach(addOn => {
                  const qty = currentPkgAddOns[addOn.item_id].qty || 0;

                  if (qty > 0) {
                    hasQty = true;
                    totalQty += qty;
                  }
                });

                const item = pkg.descrip.trim();

                const max = modMax(pkg);
                if (totalQty > max) {
                  packageAddOnsErrors.push(
                    `Maximum quantity for ${item} is ${max}.`
                  );
                }

                const min = modMin(pkg);
                if (totalQty < min) {
                  packageAddOnsErrors.push(
                    `Minimum quantity for ${item} is ${min}.`
                  );
                }

                const reqd = modReqd(pkg);
                if (!hasQty && reqd) {
                  packageAddOnsErrors.push(`An addon is required.`);
                }
              });

              if (packageAddOnsErrors.length > 0) {
                errors.packageAddOns = packageAddOnsErrors.join(' \n');
              }

              return errors;
            }}
            onSubmit={(values, { setSubmitting }) => {
              this.setState({ touched: false }, () => {
                delete values.addOnsTimes;
                actions.updateSale(values);

                actions
                  .reserve()
                  .then(() => {
                    setSubmitting(false);
                    if (config.guestMods) {
                      actions.transitionTo('/guest-info');
                    } else {
                      actions.transitionTo('/review');
                    }
                  })
                  .catch(() => {
                    setSubmitting(false);
                  });
              });
            }}
          >
            {({ isSubmitting, setFieldValue, values }) => {
              const disabled =
                isSubmitting || loading || inventory.loadingItemExpanded > 0;

              return (
                <Form>
                  <Field
                    name="packageAddOns"
                    render={({ field }) => {
                      return Object.keys(sale.packages).map(pkgId => {
                        const numPkgs = sale.packages[pkgId].qty;
                        const pkg = first(
                          inventoryPkgs,
                          p => p.item_id == pkgId
                        );
                        if (pkg == null) return null;

                        const addOns =
                          inventory.addOns[addOnKey(sale.day, pkg)] || [];

                        const total = Object.keys(
                          (values.packageAddOns[pkgId] &&
                            values.packageAddOns[pkgId]) ||
                            {}
                        ).reduce((sum, addOnId) => {
                          return (
                            sum +
                            (values.packageAddOns[pkgId][addOnId].qty || 0)
                          );
                        }, 0);

                        const maxMet = total >= modMax(pkg);
                        const minMet = total >= modMin(pkg);
                        return (
                          <PackageAddOns
                            config={config}
                            key={pkgId}
                            disabled={disabled}
                            maxMet={maxMet}
                            minMet={minMet}
                            autoAdded={modsAutoAdded}
                            value={field.value[pkgId] || {}}
                            addOns={addOns}
                            pkg={pkg}
                            num={numPkgs}
                            salePkg={sale.packages[pkgId]}
                            sale={sale}
                            onChange={value => {
                              const newFieldValue = {
                                ...field.value,
                                [pkg.item_id]: value
                              };

                              if (value == 0) {
                                delete newFieldValue[pkg.item_id];
                              }

                              setFieldValue(field.name, newFieldValue);

                              const mods = getItemModsForItemExpanded(
                                inventory,
                                sale,
                                pkg,
                                newFieldValue
                              );

                              this.checkAddOnsWithTimesHaveSelectedTime(
                                sale,
                                inventory,
                                newFieldValue
                              );

                              actions.getItemExpanded(pkg, mods);
                            }}
                          />
                        );
                      });
                    }}
                  />
                  <ErrorMessage
                    name="packageAddOns"
                    component="div"
                    className="error-message"
                  />

                  {/* pass live update before store change happens on "Continue" */}
                  <AddOnTotals
                    sale={{
                      ...sale,
                      packageAddOns: values.packageAddOns
                    }}
                    inventory={inventory}
                    config={config}
                  />

                  <FormSubmit>
                    <FormBackButton
                      disabled={disabled}
                      onClick={() => actions.transitionTo('/package')}
                    >
                      Back
                    </FormBackButton>
                    <FormSubmitButton
                      disabled={disabled || hasAddOnWithMissingTime}
                    >
                      Continue
                      {disabled ? (
                        <span>
                          &nbsp;
                          <i className="fa fa-spinner fa-pulse" />
                        </span>
                      ) : (
                        ''
                      )}
                    </FormSubmitButton>
                  </FormSubmit>
                  <FormReset>
                    <FormResetButton
                      disabled={disabled}
                      onClick={() => {
                        actions.updateSale(emptySale());
                        actions.transitionTo('/');
                      }}
                    >
                      Start Over
                    </FormResetButton>
                  </FormReset>
                </Form>
              );
            }}
          </Formik>
        )}
        <DismissableAlert>{error || inventory.error}</DismissableAlert>
      </FormWrapper>
    );
  }
}

AddOnsContainer.propTypes = {
  actions: PropTypes.object,
  error: PropTypes.string,
  config: PropTypes.object,
  loading: PropTypes.bool,
  sale: PropTypes.object,
  inventory: PropTypes.object,
  done: PropTypes.bool
};

const mapStateToProps = state => ({
  ...state.sale,
  config: state.config,
  inventory: state.inventory
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(_actions, dispatch)
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(AddOnsContainer));
